import { createSelector } from '@reduxjs/toolkit';
import { getOr, filter, pipe, size, some, get, keys, pickBy } from 'lodash/fp';
import { DATA_SOURCE_REFERENCE_DTO_TYPE } from 'app/store/__generated__/types';
import { Defaults } from 'app/lib/constants';
import { selectProjectUid } from 'app/store/projects/selectors.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { toStringFqn } from 'app/lib/utils';
import { isImportLoading, isImportAdded } from './utils.js';
import { ImportStatus } from './constants';

const selectActiveImportsBySpace = getOr(Defaults.OBJECT, 'addViews.activeImportsBySpace');

export const selectIsAddSchemaConfirmationOpen = get('addViews.addSchema.isConfirmationOpen');
export const selectAddSchemaParams = get('addViews.addSchema.params');

const selectActiveImportsByPropId = (state, props) =>
    getOr(Defaults.OBJECT, ['addViews', 'activeImportsBySpace', props.spaceId], state);

export const selectIsImporting = createSelector(selectActiveImportsByPropId, some(isImportLoading));

export const selectAddedImports = createSelector(
    selectActiveImportsByPropId,
    pipe(pickBy(isImportAdded), keys)
);

export const selectImportCount = createSelector(
    selectActiveImportsByPropId,
    pipe(filter(isImportAdded), size)
);
export const selectImportTotal = createSelector(selectActiveImportsByPropId, size);

const selectPropFqn = (_, props) => props.fqn;

export const selectIsImportingForPropFqn = createSelector(
    selectPropFqn,
    selectProjectUid,
    selectActiveImportsBySpace,
    (fqn, spaceId, imports) => {
        switch (imports[spaceId]?.[fqn]) {
            case ImportStatus.PENDING:
            case ImportStatus.ADDED:
                return true;
            default:
                return false;
        }
    }
);

export const selectError = get('addViews.error');

const selectDatasourcesInCurrentProject = createSelector(
    selectEntitiesById,
    selectProjectUid,
    (entities, projectUid) =>
        Object.values(entities).filter(
            (entity) =>
                entity._type === DATA_SOURCE_REFERENCE_DTO_TYPE && entity.spaceUid === projectUid
        )
);

export const selectDatasourcesFqnInCurrentProject = createSelector(
    selectDatasourcesInCurrentProject,
    (datasources) => datasources.map((datasource) => toStringFqn(datasource.tableFqn))
);
